import _extends from '@babel/runtime/helpers/extends';
import React, { useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { c as classNamePropTypes, a as addClassNames } from './classNames-1ed461aa.js';
import IconCross from './IconCross.js';
import { p as props, d as defaults } from './componentEl-fcbc301e.js';
import { p as paddingPropTypes } from './spacing-0f39ba4a.js';
import { B as Box } from './Box-a46c799c.js';
import { C as ConditionalFocusTrap } from './ConditionalFocusTrap-186900b2.js';

/**
Component to display a Dialog close button
*/
function DialogContent({
  children,
  padding,
  ...otherProps
}) {
  return /*#__PURE__*/React.createElement(Box.Content, _extends({
    padding: padding,
    margin: {
      all: 'none'
    }
  }, otherProps), children);
}
DialogContent.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for contents. Setting this overrides defaults and does not extend them */
  padding: paddingPropTypes
};
DialogContent.defaultProps = {
  componentEl: defaults,
  className: null,
  padding: {
    horizontal: 'mlg',
    top: 'md',
    bottom: 'mlg'
  }
};
DialogContent.displayName = 'Dialog.Content';

/**
Component to display actions at the bottom of a Dialog
*/
function DialogActions({
  componentEl: Component,
  className,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_dialog__actions'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
DialogActions.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
DialogActions.defaultProps = {
  componentEl: defaults,
  className: null
};
DialogActions.displayName = 'Dialog.Actions';

/**
Component to display a Dialog close button
*/
function DialogTitle({
  componentEl,
  className,
  padding,
  children,
  ...otherProps
}) {
  return /*#__PURE__*/React.createElement(Box.Title, _extends({
    padding: padding,
    margin: {
      all: 'none'
    }
  }, otherProps), children);
}
DialogTitle.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** padding for title. Setting this overrides defaults and does not extend them */
  padding: paddingPropTypes
};
DialogTitle.defaultProps = {
  componentEl: defaults,
  className: null,
  padding: {
    vertical: 'smd',
    left: 'mlg',
    right: 'xl'
  }
};
DialogTitle.displayName = 'Dialog.Title';

/**
Component to display a Dialog
*/
function Dialog({
  className,
  onDismiss,
  children,
  status,
  size,
  componentRef,
  renderNode,
  focusTrap,
  overflow,
  ...otherProps
}) {
  const cssClasses = ['st_dialog', `st_dialog--${status}`];
  let dialogClasses = ['st_dialog__content', `st_dialog__content--${size}`];
  dialogClasses = addClassNames(className, dialogClasses);
  if (overflow) dialogClasses.push(`st_overflow--${overflow}`);
  const contentRef = useRef(null);

  // handle when X is clicked to manually close Dialog
  const handleDismiss = useCallback(() => {
    if (onDismiss) onDismiss();
  }, [onDismiss]);

  // close when user clicks on scrim
  const listenClick = useCallback(e => {
    if (contentRef.current && !contentRef.current.contains(e.target)) {
      handleDismiss();
    }
  }, [handleDismiss, contentRef]);
  const listenKeyboard = useCallback(e => {
    if (e.key === 'Escape' || e.keyCode === 27) {
      handleDismiss();
    }
  }, [handleDismiss]);

  // listen for Esc key
  useEffect(() => {
    window.addEventListener('keydown', listenKeyboard);
    return () => window.removeEventListener('keydown', listenKeyboard);
  }, [listenKeyboard]);

  /* eslint-disable */
  // need to disable some a11y rules around clicking the scrim to close modal
  // the key event is attached to window already
  // eslint-disable jsx-a11y/click-events-have-key-events jsx-a11y/no-static-element-interactions
  const content = /*#__PURE__*/React.createElement(ConditionalFocusTrap, {
    focusTrap: focusTrap
  }, /*#__PURE__*/React.createElement("div", {
    "aria-modal": "true",
    className: cssClasses.join(' '),
    ref: componentRef,
    onPointerDown: listenClick
  }, /*#__PURE__*/React.createElement(Box, _extends({
    className: dialogClasses.join(' ')
  }, otherProps, {
    padding: {
      all: 'none'
    },
    margin: null,
    role: "dialog",
    componentRef: contentRef
  }), /*#__PURE__*/React.createElement("div", {
    className: "st_dialog__closewrap"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": "dismiss",
    size: "md",
    className: "st_dialog__close",
    onClick: handleDismiss
  }, /*#__PURE__*/React.createElement(IconCross, {
    alt: "dismiss"
  }))), children)));
  /* eslint-enable */

  if (status === 'initial' || status === 'closed') return null;
  if (!renderNode) return content;
  return /*#__PURE__*/ReactDOM.createPortal(content, renderNode);
}
Dialog.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** callback when Dialog is dismissed, either via the X icon, or by pressing Esc */
  onDismiss: PropTypes.func,
  /** node where Dialog should be rendered */
  renderNode: PropTypes.any,
  /** status of Dialog, generally should be open or closed */
  status: PropTypes.oneOf(['initial', 'beforeopen', 'opening', 'closing', 'closed', 'open']),
  /** size of Dialog */
  size: PropTypes.oneOf(['fluid', 'sm', 'md', 'lg', 'xl']),
  /** ref for element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** whether to trap focus when Dialog is open, for accessibility */
  focusTrap: PropTypes.bool,
  /** overflow behavior when dialog contents are larger than the available viewport space */
  overflow: PropTypes.oneOf(['auto', 'visible', 'hidden', 'scroll'])
};
Dialog.defaultProps = {
  className: null,
  onDismiss: () => null,
  renderNode: null,
  status: 'initial',
  componentRef: null,
  size: 'md',
  focusTrap: true,
  overflow: 'auto'
};
Dialog.displayName = 'Dialog';
Dialog.Title = DialogTitle;
Dialog.Content = DialogContent;
Dialog.Actions = DialogActions;

export { Dialog as D };
